import { Injectable, signal, computed, inject, effect } from '@angular/core';
import { PanelStylesService } from '../panel-styles/panel-styles.service';

export type MaterialType = 'raceway' | 'laminate' | 'fabric' | 'glass' | 'pet';

export interface MaterialSelection {
  type: MaterialType | null;
  color: string | null;
}

export interface MaterialColor {
  code: string;
  name: string;
}

@Injectable({ providedIn: 'root' })
export class MaterialService {
  private panelStylesService = inject(PanelStylesService);

  readonly allTypes: MaterialType[] = ['fabric', 'pet'];

  private colorsByType: Record<MaterialType, MaterialColor[]> = {
    raceway: [{ code: 'RW', name: 'Raceway' }],
    laminate: [
      { code: 'W960',  name: 'Classic White' },
      { code: 'XD_26', name: 'Sea Salt' },
      { code: 'XD_25', name: 'Black Oak' },
      { code: 'XD_21', name: 'Driftwood' },
      { code: 'WA7949', name: 'Asian Night' }
    ],
    fabric: [
      { code: '15_104', name: 'Banana' }, { code: '15_107', name: 'Blue Lagoon' },
      { code: '15_09',  name: 'Toffee' },  { code: '15_118', name: 'Lime Green' },
      { code: '15_75F', name: 'Pumpkin' }, { code: '15_13',  name: 'Charcoal' },
      { code: '15_175', name: 'Cool Aqua' }, { code: '15_155', name: 'Gold' },
      { code: '15_03',  name: 'Midnight' },  { code: '15_164', name: 'Scarlet' },
      { code: '15_167', name: 'Royal Blue' }, { code: '15_103', name: 'Sage' },
      { code: '15_108', name: 'Silver' },     { code: '15_41',  name: 'Slate' },
      { code: '15_109', name: 'Caramel' }
    ],
    glass: [{ code: 'GLASS', name: 'Clear Glass' }, {code: 'FGLASS', name: 'Frosted Glass'}],
    pet: [
      { code: 'AC_44', name: 'Black' },      { code: 'AC_37', name: 'Blue' },
      { code: 'AC_43', name: 'Dark Blue' },  { code: 'AC_14', name: 'Emerald Green' },
      { code: 'AC_17', name: 'Red' },        { code: 'AC_53', name: 'Ocean' },
      { code: 'AC_32', name: 'Orange' },     { code: 'AC_23', name: 'Orange Yellow' },
      { code: 'AC_36', name: 'Purple' },     { code: 'AC_05', name: 'Sand Dune' },
      { code: 'AC_25', name: 'Green' },      { code: 'AC_40', name: 'Coffee' },
      { code: 'AC_12', name: 'Sesame Black' }, { code: 'AC_07', name: 'Sky Blue' },
      { code: 'AC_10', name: 'Silver Gray' },  { code: 'AC_04', name: 'White' },
      { code: 'AC_58', name: 'Peacock' },      { code: 'AC_65', name: 'Seaweed' }
    ]
  };

  /** Defaults */
  private selections = signal<Record<string, MaterialSelection>>({
    T0: { type: 'raceway',  color: this.colorsByType['raceway'][0].code },
    T1: { type: 'laminate', color: this.colorsByType['laminate'][0].code },
    T2: { type: 'laminate', color: this.colorsByType['laminate'][0].code },
    T3: { type: 'laminate', color: this.colorsByType['laminate'][0].code }
  });

  constructor() {
    // 🔁 Single, central effect that keeps selections valid/reactive
    effect(
      () => {
        const style = this.panelStylesService.selectedStyle(); // signal read
        const current = this.selections();                      // signal read
        const zones = Object.keys(current) as Array<keyof typeof current>;

        zones.forEach(zone => {
          const types = this.computeAvailableTypes(style, zone); // pure calculation
          const sel = current[zone];
          let desiredType = sel?.type ?? null;

          /**
          if (zone === 'T3') {
            desiredType = 'glass';
          }

          if (zone === 'T2' || zone === 'T1') {
            desiredType = 'laminate';
          }

          if (zone === 'T0') {
            desiredType = 'raceway';
          }
          **/

          if (!desiredType || !types.includes(desiredType)) {
            // If you want hard rules for specific zones, do it *only* when needed:
            if (zone === 'T3' && types.includes('glass')) desiredType = 'glass';
            else if (zone === 'T0' && types.includes('raceway')) desiredType = 'raceway';
            else desiredType = types[0] ?? 'laminate';
          }

          // If missing or invalid, default to laminate
          if (!desiredType || !types.includes(desiredType)) {
            desiredType = 'laminate';
          }

          // Pick color: keep existing if still valid type; else first of desired type
          const desiredColor =
            sel?.type === desiredType && sel?.color
              ? sel.color
              : this.colorsByType[desiredType][0]?.code ?? null;

          if (sel?.type !== desiredType || sel?.color !== desiredColor) {
            this.selections.update(prev => ({
              ...prev,
              [zone]: { type: desiredType, color: desiredColor }
            }));
          }
        });
      },
      { allowSignalWrites: true } // 👈 important: effect is allowed to write signals
    );
  }

  /** Expose selection for a zone */
  getSelection(zone: string) {
    return computed(() => this.selections()[zone]);
  }

  /** ✅ PURE computed: no writes here */
  getAvailableTypes(zone: string) {
    return computed(() =>
      this.computeAvailableTypes(this.panelStylesService.selectedStyle(), zone)
    );
  }

  /** PURE helper used by both the effect and the computed above */
  private computeAvailableTypes(style: string | null, zone: string): MaterialType[] {
    let types = [...this.allTypes];

    if (zone === 'T3') {
      types = ['glass'];
    }

    return types;
  }

  getColors(type: MaterialType | null): MaterialColor[] {
    return type ? this.colorsByType[type] : [];
  }

  setType(zone: string, type: MaterialType) {
    const defaultColor = this.getColors(type)[0]?.code ?? null;
    this.selections.update(current => ({
      ...current,
      [zone]: { type, color: defaultColor }
    }));
  }

  setColor(zone: string, code: string) {
    this.selections.update(current => ({
      ...current,
      [zone]: { ...current[zone], color: code }
    }));
  }

  getColorLabel(type: MaterialType | null, code: string | null): string | null {
    if (!type || !code) return null;
    return this.colorsByType[type].find(c => c.code === code)?.name ?? null;
  }
}
